// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { nativeScrollViewProps } from 'components/common/scroll/TreactNativeScrollStyles';
import useUsersApi from 'hooks/useUsersApi';
import useRolesApi from 'hooks/useRolesApi';
import { User } from 'model/User';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UserDetails from './Users/UserDetails';
import PageContent from 'components/treactui-template/organisms/page/PageContent';
import useFilteredUsers from 'hooks/useFilteredUsers';
import SelectUser from './Users/SelectUser';
import useMediaQuery from 'hooks/useMediaQuery';
import { Colors, Table } from '@tls/treact-ui';
import { useAppSelector } from 'store/hooks';
import HighlightText from 'components/common/HighlightText';
import sortBy from 'lodash/sortBy';
import styled from 'styled-components';
import TestableLoader from 'components/treactui-template/atoms/TestableLoader';

export default function Users() {
  const { users, loadingUsers, editUser, deleteUser } = useUsersApi();
  const { roles } = useRolesApi();
  const { t } = useTranslation();

  const filterText = useAppSelector(s => s.uiSettings.filterText);
  const filteredUsers = useFilteredUsers(users, roles);
  const { isMedium, isSmall, isExtraSmall } = useMediaQuery();
  const isSmallWidth = isMedium || isSmall || isExtraSmall;

  const roleNames = useCallback(
    (user: User) => user.roles?.map(roleId => roles?.find(role => role.id === roleId)?.name ?? roleId).join(', '),
    [roles]
  );

  const [selectedUserId, setSelectedUserId] = useState<string>();

  // Clear selection on small width so the list shows instead of the detail view.
  useEffect(() => {
    if (isSmallWidth) {
      setSelectedUserId(undefined);
    }
  }, [isSmallWidth]);

  // Auto-select first user once the fetch resolves (wide layout, nothing picked yet).
  useEffect(() => {
    if (!isSmallWidth && selectedUserId === undefined && filteredUsers && filteredUsers.length > 0) {
      setSelectedUserId(filteredUsers[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react to data arriving, not filter/selection churn
  }, [filteredUsers]);

  const selectedUser = filteredUsers?.find(user => user.id === selectedUserId);

  const sortArray = useCallback((array: User[], sortProp: string) => sortBy(array, sortProp), []);
  const onSortByUserName = useCallback((array: User[]) => sortArray(array, 'userName'), [sortArray]);
  const onSortByEmail = useCallback((array: User[]) => sortArray(array, 'email'), [sortArray]);
  const onSortByRoles = useCallback((array: User[]) => sortBy(array, roleNames), [roleNames]);

  return (
    <PageContent displayAsPaper disableScroll>
      <Root>
        {(!isSmallWidth || selectedUserId === undefined) && (
          <ListSector $showBorder={!isSmallWidth}>
            <Table list={filteredUsers ?? []}>
              {(sortedUsers: User[]) => (
                <>
                  <Table.Header>
                    <Table.HeaderRow>
                      <Table.HeaderSortCell
                        width='15rem'
                        sortFn={onSortByUserName}
                        sortKey='userName'
                        data-testid='settings-users-name-sort-button'
                      >
                        {t('user.userName')}
                      </Table.HeaderSortCell>
                      <Table.HeaderSortCell
                        width='15rem'
                        sortFn={onSortByEmail}
                        sortKey='email'
                        data-testid='settings-users-email-sort-button'
                      >
                        {t('user.email')}
                      </Table.HeaderSortCell>
                      <Table.HeaderSortCell
                        width='15rem'
                        sortFn={onSortByRoles}
                        sortKey='roles'
                        data-testid='settings-users-roles-sort-button'
                      >
                        {t('user.roles')}
                      </Table.HeaderSortCell>
                    </Table.HeaderRow>
                  </Table.Header>
                  <Table.ScrollingBody scrollViewProps={nativeScrollViewProps}>
                    {loadingUsers && <TestableLoader label='' data-testid='settings-users-loader' />}
                    {sortedUsers.map(user => (
                      <Table.Row
                        key={user.id}
                        className={user.id === selectedUserId ? 'selected' : ''}
                        onClick={() => setSelectedUserId(user.id)}
                        data-testid={`settings-user-${user.id ?? 'unknown'}-row`}
                      >
                        <Table.Cell width='15rem' data-testid={`settings-user-${user.id ?? 'unknown'}-row-name`}>
                          <NameCell>
                            <NameRow className='font-size_m font-weight_bold'>
                              <HighlightText text={user.userName} query={filterText} />
                            </NameRow>
                            <NameRow className='font-size_s'>
                              <HighlightText
                                text={`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()}
                                query={filterText}
                              />
                            </NameRow>
                          </NameCell>
                        </Table.Cell>
                        <Table.Cell width='15rem' data-testid={`settings-user-${user.id ?? 'unknown'}-row-email`}>
                          <HighlightText text={user.email} query={filterText} />
                        </Table.Cell>
                        <Table.Cell width='15rem' data-testid={`settings-user-${user.id ?? 'unknown'}-row-roles`}>
                          <HighlightText text={roleNames(user)} query={filterText} />
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.ScrollingBody>
                </>
              )}
            </Table>
          </ListSector>
        )}
        {(!isSmallWidth || selectedUserId !== undefined) && (
          <DetailSector>
            {!selectedUser ? (
              <SelectUser />
            ) : (
              <UserDetails
                user={selectedUser}
                users={users}
                isLoading={loadingUsers}
                addOrEditUser={editUser}
                isUsernameEditable={false}
                deleteUser={deleteUser}
                onClose={isSmallWidth ? () => setSelectedUserId(undefined) : undefined}
              />
            )}
          </DetailSector>
        )}
      </Root>
    </PageContent>
  );
}

const Root = styled.div`
  display: flex;
  height: 100%;
  overflow: hidden;
`;

const ListSector = styled.div<{ $showBorder: boolean }>`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-right: ${({ $showBorder }) => ($showBorder ? `1px solid ${Colors.trgrey2.hex}` : 'none')};
`;

const DetailSector = styled.div`
  flex: 1;
  overflow: hidden;
`;

const NameCell = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const NameRow = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;
